import { defineStore } from 'pinia'
import { useFlag } from '@unleash/proxy-client-vue'
import * as Sentry from '@sentry/vue'
import type { AxiosResponse } from 'axios'
import { DEFAULT_DEMO_SPACE_ID } from '@constants/demo'
import { PREVIEW_EDITOR_FORM_MINIMUM_SIZE } from '@constants/editorBreakpoints'
import LOCAL_STORAGE_KEYS from '@constants/localStorageKeys'
import type { RegionServerKeys } from '@/constants/region'
import { USER_ROLES } from '@/constants/user'
import { client } from '@/services/client'
import { handleRequest } from '@/services/handleRequest'
import type { SafeAxiosResponse } from '@/services/handleRequest'
import type { DemoTheme } from '@/store/modules/App/content'
import { useContent } from '@/store/modules/App/content'
import { useUser } from '@/store/modules/App/user'
import { useWorkflows } from '@/store/modules/App/workflows'
import {
  findLanguageIndexBySpaceId,
  getLanguageSettings,
  setLanguageSettings,
} from '@/store/utils/languageSettings'
import type { LangPerSpace } from '@/store/utils/languageSettings'
import { Permissions } from '@/types'
import type {
  ApiKey,
  AppProvision,
  BackgroundTask,
  Backups,
  Collaborator,
  FeatureLimit,
  InvitedSpaceCollaborator,
  Locale,
  AssetMetadataSchema,
  DefaultMetadataKey,
  Space,
  SpaceOption,
  SpaceRole,
  SpaceStatistics,
  User,
  AssetSignature,
  CreateSpace,
  BackgroundTasks,
  CollaboratorModel,
} from '@/types'
import { StripeTrialStatuses } from '@/types/space'
import type {
  FeatureLimitExceededFlags,
  LastBoostInvoice,
  SpaceEnvironment,
} from '@/types/space'
import { isEmpty } from '@utils/checkers'
import { isSpaceWithoutPlan, isExpiredTrial } from '@utils/getters'
import { isPLGPlan } from '@utils/getters/getSpacePlanType'
import { numberFromString } from '@utils/parsers'
import { toggleUnleashSpace } from '@utils/setup-application/createUnleash'
import { usePermissionsPlugin } from '@/utils/helpers/permissionsHelper'
import { t } from '@/utils/helpers/translate'

export type State = {
  apiKeys: ApiKey[]
  backgroundTasks: BackgroundTask[]
  canSubscribe: boolean
  editorFormOpen: boolean
  floating: boolean
  installedApps: AppProvision[]
  lang: string
  model: Space
  previewFormWidth: number
  region?: RegionServerKeys | null
  spaceStatistics: SpaceStatistics
  spaceFeatureLimits: Record<string, FeatureLimit>
  lastBoostInvoice: LastBoostInvoice
  hasOnboardingImprovements: boolean
}

const defaultState = {
  apiKeys: [],
  backgroundTasks: [],
  canSubscribe: false,
  editorFormOpen: false,
  floating: false,
  installedApps: [],
  lang: '',
  model: {} as Space,
  previewFormWidth: PREVIEW_EDITOR_FORM_MINIMUM_SIZE,
  region: null,
  spaceStatistics: {} as SpaceStatistics,
  spaceFeatureLimits: {},
  lastBoostInvoice: {} as LastBoostInvoice,
  hasOnboardingImprovements: false,
}

const baseEmptyRole = (): Omit<
  SpaceRole,
  'ext_id' | 'id' | 'role' | 'subtitle'
> => ({
  permissions: [],
  field_permissions: [],
  readonly_field_permissions: [],
  allowed_path: '',
  allowed_paths: [],
  resolved_allowed_paths: [],
  datasource_ids: [],
  asset_folder_ids: [],
  component_ids: [],
  branch_ids: [],
  allowed_languages: [],
  blocked_languages: [],
  blocked_paths: [],
  blocked_asset_folder_ids: [],
  blocked_branch_ids: [],
  blocked_datasource_ids: [],
  allowed_field_permissions: [],
  allowed_component_ids: [],
  resolved_blocked_paths: [],
  managed_component_ids: [],
  blocked_manage_component_ids: [],
  managed_component_group_uuids: [],
  blocked_manage_component_group_uuids: [],
  blocked_component_group_uuids: [],
  component_group_uuids: [],
})

export type SpaceStore = ReturnType<typeof useSpace>

export const useSpace = defineStore('space', {
  state: (): State => defaultState,
  getters: {
    // general
    space: (state: State): Space => state.model,
    spaceId: (state: State): number => state.model.id ?? 0,
    getSpaceOptions: (state: State): SpaceOption => state.model.options!,
    getSpaceLimits: (state: State): SpaceOption => state.model.limits!,
    getInstalledApps: (state: State): AppProvision[] => state.installedApps,
    getAppsWithSettings: (state: State): AppProvision[] =>
      state.installedApps.filter((item) => item.enable_space_settings),
    getApiKeys: (state: State): ApiKey[] => state.apiKeys,
    getSpaceLanguages: (state: State): Locale[] => state.model.languages || [],
    getSpaceRegion: (state: State): RegionServerKeys | undefined | null =>
      state.region,
    isEuSpace: (state: State): boolean =>
      state.region?.startsWith('eu-') || false,
    getDefaultSpaceContentType: (state: State): string =>
      state.model.default_root,
    getSpaceBackgroundTasks: (state: State): BackgroundTask[] =>
      state.backgroundTasks,
    getDefaultLangName: (state: State): string =>
      state.model.default_lang_name as string,
    getSpaceParentId: (state: State): number | undefined =>
      state.model?.parent_id ?? undefined,
    getSpaceStatistics: (state: State): SpaceStatistics =>
      state.spaceStatistics,
    getLastBoostInvoice: (state: State): LastBoostInvoice =>
      state.lastBoostInvoice,
    getSpaceDefaultLanguage: (state: State): string =>
      (state.model.default_lang_name as string) || '',
    getSpaceName: (state: State): string => state.model.name || '',
    hasStripeAccountAssociation: (state: State): boolean =>
      Boolean(state.model.euid), // euid is the stripe account id
    spaceDomain: (state: State): string => {
      const envSettings = JSON.parse(
        localStorage.getItem(LOCAL_STORAGE_KEYS.settings) as string,
      )
      let env = ''

      if (envSettings && state.model.id && envSettings.envs[state.model.id]) {
        env = envSettings.envs[state.model.id]
      }

      const envInfo = state.model.environments
        ? state.model.environments.find(($e) => $e.name === env)
        : ''

      if (envInfo) {
        return envInfo.location
      }
      return state.model.domain
    },

    // editor settings
    allowedLanguagesCodes(): string[] {
      return this.userPermissions.allowed_languages
    },
    allowedLanguages(): Locale[] {
      let allowedlanguages =
        this.allowedLanguagesCodes.length === 0
          ? [...(this.space.languages ?? [])]
          : [...(this.space.languages ?? [])].filter((lang) =>
              this.allowedLanguagesCodes.includes(lang.code),
            )

      if (
        (this.allowedLanguagesCodes.length === 0 ||
          this.allowedLanguagesCodes.includes('default')) &&
        !allowedlanguages.find(
          (lang) => lang.code === this.defaultLanguage.code,
        )
      ) {
        allowedlanguages.unshift(this.defaultLanguage)
      }

      if (this.userPermissions?.blocked_languages.length > 0) {
        allowedlanguages = allowedlanguages.filter(
          (lang) =>
            !this.userPermissions.blocked_languages.includes(
              lang.code || 'default',
            ),
        )
      }

      return allowedlanguages
    },
    defaultLanguage: (state: State): Locale => ({
      name: state.model.default_lang_name || '',
      code: '',
      ai_translation_code: 'en',
    }),
    getLang: (state: State): string => state.lang.replace(/^default$/, ''),
    getCurrentLanguage(state: State): Locale {
      if (!state.lang || state.lang === 'default') {
        return this.defaultLanguage
      }
      const lang: Locale | undefined = state.model.languages?.find(
        (l) => l.code === state.lang,
      )
      return lang ?? this.defaultLanguage
    },
    getFormWidth: (state: State): number => state.previewFormWidth,

    getSpaceActiveLanguageName: (state: State): string => {
      const lang: Locale | undefined = state.model.languages?.find(
        (l) => l.code === state.lang,
      )
      return lang?.name ?? 'Default'
    },
    getIsFloating: (state: State): boolean => state.floating,
    getEditorFormOpen: (state: State): boolean => state.editorFormOpen,
    getSpaceMetaDataSchema: (state: State): AssetMetadataSchema[] =>
      state.model?.asset_custom_meta_data_schema || [],
    hasTranslatableMetadata: (state: State): boolean =>
      state.model?.asset_custom_meta_data_schema?.some(
        (schema) => schema.translatable,
      ) || (state.model?.translatable_asset_fields?.length ?? 0) > 0,
    getRequiredAssetFields: (state: State): DefaultMetadataKey[] =>
      state.model?.required_assest_fields || [],
    getTranslatableAssetFields: (state: State): DefaultMetadataKey[] =>
      state.model?.translatable_asset_fields || [],
    getBlueprintPreviewEnvironment: (
      state: State,
    ): SpaceEnvironment | undefined =>
      state.model?.environments?.find(
        (environment) => !!environment.from_blueprint,
      ),
    // checkers
    isDemoSpace: (state: State): boolean | undefined =>
      state.model?.is_demo || undefined,
    isMaintenanceMode: (state: State): boolean =>
      state.model?.maintenance || false,
    getSpaceOwnerId: (state: State): number => state.model?.owner_id,
    isSpaceOwner(): boolean {
      const { user } = useUser()
      return this.space.owner_id === user.id
    },
    isSpaceEditor: (state: State): boolean => {
      return state.model.role === 'editor'
    },
    isSpaceAdmin: (state: State): boolean => {
      const { user } = useUser()
      const canActAsAdmin =
        state.model?.collaborators?.find(
          (collaborator: Collaborator): boolean =>
            collaborator.user_id === user.id,
        )?.can_act_as_admin ?? false

      return state.model.role === 'admin' || canActAsAdmin
    },
    isBackToV1Disabled: (state: State): boolean => {
      return (
        state.model?.region?.startsWith('us-') ||
        (isExpiredTrial(state.model) && isSpaceWithoutPlan(state.model)) ||
        false
      )
    },
    isBlueprintSetupIncomplete: (state: State): boolean =>
      Boolean(state.model.blueprint_reference),
    isBlueprintSpace: (state: State): boolean => {
      const previewEnvironment = state.model?.environments?.find(
        (environment) => !!environment.from_blueprint,
      )

      return Boolean(state.model.repository_url) || Boolean(previewEnvironment)
    },

    // roles & users
    spaceRole: (state: State): string | null => state.model.role || null,
    getSpaceRoles: (state: State): SpaceRole[] => state.model.space_roles,
    getUserCanSubscribe: (state: State): boolean => state.canSubscribe,
    getFeatureLimitExceededFlags:
      (state: State) =>
      (prop: keyof FeatureLimitExceededFlags): boolean =>
        state.model.feature_limit_exceeded_flags?.[prop] || false,
    collaborators(): Collaborator[] {
      const users: Collaborator[] = []

      if (this.space.owner) {
        users.push({
          user: this.space.owner,
          role: USER_ROLES.owner,
          user_id: 0, // this is the Collaborator ID and not the User ID, since the owner is not included in the collaborators we could use a fake ID like 0
          permissions: [],
          allowed_path: '',
          field_permissions: [],
          id: 0,
          invitation: null,
          space_role_id: null,
          space_role_ids: [],
          space_id: this.space.id,
          can_act_as_admin: false,
        })
      }
      if (this.space.collaborators?.length > 0) {
        users.push(
          ...[...(this.space.collaborators ?? [])].sort((a, b) => {
            if (a.user?.friendly_name < b.user?.friendly_name) {
              return -1
            }
            if (a.user?.friendly_name > b.user?.friendly_name) {
              return 1
            }
            return 0
          }),
        )
      }

      return users
    },
    /**
     * This getter is used to get the collaborator object based on the user_id, if the user is not found it will return an object with "Unknown" user
     *
     * @returns { Function } A function that returns the collaborator object based on the user_id
     */
    getCollaborator(): (userId: number) => User | Partial<User> {
      const collaborators = this.collaborators
      return (userId: number): User | Partial<User> =>
        collaborators.find((user) => user.user_id === userId)?.user || {
          id: userId,
          friendly_name: 'Unknown',
          disabled: true,
        }
    },
    /**
     *
     * @deprecated consider using `getCollaborator` instead that already contains the owner and, if the user is not found, it will return an object with "Unknown" user
     */
    getSpaceUserInfo:
      (state: State) =>
      (id: number | null): User | Record<string, never> => {
        const userStore = useUser()
        if (id === userStore.user?.id) {
          return userStore.user
        } else if (id === state.model?.owner?.id) {
          return state.model.owner
        }

        const collab = state.model.collaborators?.find((collaborator) => {
          return collaborator.user?.id === id
        })

        // returns null if the collab doesn't belong to the space
        if (!collab) {
          return {}
        }

        return collab.user
      },

    /**
     * @deprecated use `userPermissions` getter instead, which is an object with all permissions together and no longer an array
     */
    spaceRoles(): Omit<SpaceRole, 'ext_id' | 'id' | 'role' | 'subtitle'>[] {
      return this.userRolesPermissions.map((role) => {
        let paths = ''

        if (
          role.resolved_allowed_paths?.length > 0 ||
          role.resolved_blocked_paths?.length > 0
        ) {
          const key =
            role.resolved_allowed_paths.length > 0
              ? 'resolved_allowed_paths'
              : 'resolved_blocked_paths'

          paths = `(${role[key].join('.*|').replace(/\//g, '\\/')}.*)`
        }

        const fieldPermissions = role.field_permissions.map(
          (item) => `^${item}._hide$`,
        )

        return {
          permissions: role.permissions,
          field_permissions: fieldPermissions,
          readonly_field_permissions: role.readonly_field_permissions,
          component_ids: role.component_ids,
          allowed_path: role.resolved_allowed_paths?.length > 0 ? paths : '',
          datasource_ids: role.datasource_ids,
          asset_folder_ids: role.asset_folder_ids,
          branch_ids: role.branch_ids,
          allowed_languages: role.allowed_languages,
          allowed_paths: role.allowed_paths,
          resolved_allowed_paths: role.resolved_allowed_paths,
          blocked_languages: role.blocked_languages,
          blocked_path: role.resolved_blocked_paths?.length > 0 ? paths : '',
          blocked_paths: role.blocked_paths,
          blocked_asset_folder_ids: role.blocked_asset_folder_ids,
          blocked_branch_ids: role.blocked_branch_ids,
          blocked_datasource_ids: role.blocked_datasource_ids,
          allowed_field_permissions: role.allowed_field_permissions,
          allowed_component_ids: role.allowed_component_ids,
          resolved_blocked_paths: role.resolved_blocked_paths,
          managed_component_ids: role.managed_component_ids,
          blocked_manage_component_ids: role.blocked_manage_component_ids,
          managed_component_group_uuids: role.managed_component_group_uuids,
          blocked_manage_component_group_uuids:
            role.blocked_manage_component_group_uuids,
          blocked_component_group_uuids: role.blocked_component_group_uuids,
          component_group_uuids: role.component_group_uuids,
        }
      })
    },

    /**
     * This getter is used to get the user's permissions per role
     *
     * @param state
     * @returns { Array } Array of roles with permissions
     */
    userRolesPermissions: (
      state: State,
    ): Omit<SpaceRole, 'ext_id' | 'id' | 'role' | 'subtitle'>[] => {
      const getRoles = (ids: number[]): SpaceRole[] => {
        return state.model?.space_roles?.filter((role) => {
          return ids.includes(role.id)
        })
      }

      const userStore = useUser()
      const user = state.model?.collaborators?.find(
        (collaborator) => collaborator.user_id === userStore.user.id,
      )

      // @TODO: verify this! Is this a real case? Could a space really have no users?
      // at the moment since the unit test of this file is based on MOCKED data and not tested after setting a store
      // therefore no user is loaded and the test will always fallback on the condition with no users
      if (user) {
        if (user.role === 'multi') {
          return getRoles(user.space_role_ids)
        } else if (
          user.role &&
          user.space_role_id &&
          !['admin', 'editor', 'advanced'].includes(user.role)
        ) {
          return getRoles([user.space_role_id])
        } else if (user.role === 'advanced') {
          return [
            {
              ...baseEmptyRole(),
              permissions: user.permissions,
              field_permissions: user.field_permissions,
              allowed_path: user.allowed_path,
            },
          ]
        }
      }

      // fallback for 'admin' and 'editor' roles and also if no user is found (??)
      return [
        {
          ...baseEmptyRole(),
          permissions: [Permissions.All],
        },
      ]
    },

    /**
     * This getter is used to get all the user's permissions in one merged object
     *
     * @param state
     * @returns { Object } Object with all the permissions of the current user merged together
     */
    userPermissions(): Omit<SpaceRole, 'ext_id' | 'id' | 'role' | 'subtitle'> {
      const roles = this.userRolesPermissions

      const permissions: Omit<
        SpaceRole,
        'ext_id' | 'id' | 'role' | 'subtitle'
      > = {
        ...baseEmptyRole(),
        // @ts-expect-error - the correct will be an string array but to merge the permissions we need to use an array of arrays first
        allowed_path: [],
      }

      // users can have multiple roles, this is to merge all the roles into one
      const userRolesPermissions = roles.reduce(
        (acc: Omit<SpaceRole, 'ext_id' | 'id' | 'role' | 'subtitle'>, role) => {
          const properties = Object.keys(permissions)

          properties.forEach((prop) => {
            if (role[prop as keyof typeof role]) {
              ;(acc[prop as keyof typeof acc] as unknown[]).push(
                role[prop as keyof typeof role],
              )
            }
          })

          return acc
        },
        permissions,
      )

      return Object.fromEntries(
        Object.entries(userRolesPermissions).map(([permissionName, value]) => {
          /**
           * For all the permissions settings that acts like allow-lists,
           * if at least one role assigned to the user is an empty list of permissions,
           * then this rule overrides the other rules
           */
          let permissionValue = !value.find((arr) => arr.length === 0)
            ? value.flat()
            : []

          switch (permissionName) {
            // Special handling for component management permissions - always merge all values
            case 'managed_component_ids':
            case 'managed_component_group_uuids':
            case 'blocked_manage_component_ids':
            case 'blocked_manage_component_group_uuids': {
              permissionValue = Array.isArray(value) ? value.flat() : []
              break
            }
            /**
             * This array of strings is remapped to regex strings to keep compatibility with the v1.
             * We may not this and maybe we can remove this once v1 is stopped
             * but we need to make sure it's not attached to some data saved in the database.
             *
             * To use it as single regex string as in the original one,
             * you can join the strings with a pipe (|) and wrap it with parentheses, ie: `(${permissionValue.join('|')})`
             */
            case 'field_permissions': {
              permissionValue = permissionValue.map((item) => `^${item}._hide$`)

              break
            }
            case 'permissions': {
              /**
               * if at least one role assigned to the user allows to manage non-translatable fields in non-default language,
               * then the user can manage them
               */
              if (
                value.some(
                  (arr) =>
                    !arr.includes(Permissions.ManageNonTranslatableFields),
                )
              ) {
                permissionValue = permissionValue.filter(
                  (item) => item !== Permissions.ManageNonTranslatableFields,
                )
              }
              break
            }

            /**
             * This array of strings is remapped to regex strings.
             *
             * To use it as single regex string as in the original one,
             * you can join the strings with a pipe (|) and wrap it with parentheses, ie: `(${permissionValue.join('|')})`
             */
            case 'resolved_allowed_paths': {
              permissionValue = permissionValue.map(
                (item) => `${item.toString().replace(/\//g, '\\/')}.*`,
              )
              break
            }
          }

          return [permissionName, permissionValue]
        }),
      )
    },
    getFeatureLimits: (state: State): Record<string, FeatureLimit> => {
      return state.spaceFeatureLimits || {}
    },
    getSpaceDemoTheme: (state: State): DemoTheme | undefined => {
      const content = useContent()

      const { themes = [] } = content.getDemoDetails
      const duplicatedFromId =
        state.model.options?.duplicated_from || DEFAULT_DEMO_SPACE_ID.toString()
      const themeCheckCb = (theme: DemoTheme) =>
        numberFromString(theme.spaceId) === numberFromString(duplicatedFromId)
      return themes.find(themeCheckCb)
    },
    isSpaceDuplicatedFromDemo(): boolean {
      return this.getSpaceDemoTheme !== undefined
    },
    isSpaceTrialPending(): boolean {
      return this.space.stripe_trial_status === StripeTrialStatuses.PENDING
    },
    isSpacePendingSubscription(state: State): boolean {
      const hasPendingTrial =
        this.isSpaceTrialPending &&
        (isSpaceWithoutPlan(this.space) || !!this.space.canceled_at)

      let hasPendingTrialAndEnded = false

      if (this.isSpaceTrialPending && this.space.stripe_trial_end) {
        const trialEnd = new Date(this.space.stripe_trial_end).getTime()
        const now = Date.now()
        hasPendingTrialAndEnded = !isNaN(trialEnd) && now > trialEnd
      }

      return state.hasOnboardingImprovements
        ? hasPendingTrialAndEnded
        : hasPendingTrial
    },
    hasSubscriptionPageReadOnlyAccess(): boolean {
      const { getUserInfo: user } = useUser()

      if (!user || this.isSpaceOwner || this.canSubscribe) {
        return false
      }

      return user.role === USER_ROLES.superuser && isPLGPlan(this.space)
    },
    shouldEnableDemoLogic(): boolean {
      return Boolean(this.isDemoSpace) && this.isSpaceDuplicatedFromDemo
    },
    getAssetSignatures: (state): AssetSignature => {
      return state.model.asset_signature || {}
    },
    hasPrivateAssets: (state): boolean => {
      return state.model.private_assets || false
    },
    isBlockedOrUnpaid: (state): boolean => {
      return Boolean(state.model.blocked_at || state.model.unpaid_at)
    },
  },
  actions: {
    checkOnboardingImprovements(): void {
      this.hasOnboardingImprovements = useFlag('onboarding-improvements').value
    },
    setSpace(space: Space): Space {
      this.model = space
      this.spaceFeatureLimits = space?.feature_limits?.reduce(
        (acc: Record<string, FeatureLimit>, limit: FeatureLimit) => {
          acc[limit.key] = limit
          return acc
        },
        {},
      )
      return this.space
    },

    setSpaceRegion(region?: RegionServerKeys | null): void {
      this.region = region
    },

    resetSpace(): void {
      // @ts-expect-error -- TODO {} is not assignable to the model
      this.model = {}
      this.spaceFeatureLimits = {}
      this.region = undefined
    },

    setSpaceLanguage(langCode?: string): void {
      const languagesInLocalStorage = getLanguageSettings()
      const languageInLocalStorage =
        languagesInLocalStorage.find((item) => item.spaceId === this.space.id)
          ?.lang || ''

      const languageCode = (langCode ?? languageInLocalStorage) || 'default'

      const matchedLanguage = this.allowedLanguages.find(
        ({ code }) => code === languageCode,
      )

      // Set language with priority: matched language > first allowed language > empty string
      const languageToSet =
        matchedLanguage?.code ??
        (this.allowedLanguages.length > 0 && this.allowedLanguages[0]?.code
          ? this.allowedLanguages[0].code
          : '')

      this.lang = languageToSet.replace(/^default$/, '')

      // finally update the language settings in the localstorage
      const currentSpaceIndex = findLanguageIndexBySpaceId(this.space.id)
      const objectToSetOnSession: LangPerSpace = {
        spaceId: this.space.id,
        lang: this.lang,
      }

      if (currentSpaceIndex >= 0) {
        languagesInLocalStorage[currentSpaceIndex] = objectToSetOnSession
      } else {
        languagesInLocalStorage.push(objectToSetOnSession)
      }

      setLanguageSettings(languagesInLocalStorage)
    },

    setInstalledApps(apps: AppProvision[]): void {
      this.installedApps = apps
    },

    setApiKeys(apiKeys: ApiKey[]): void {
      this.apiKeys = apiKeys
    },

    setFormWidth(width: number): undefined {
      const halfOfScreen = window.innerWidth / 2
      if (width >= halfOfScreen) {
        this.previewFormWidth = halfOfScreen
        return
      }

      if (width < PREVIEW_EDITOR_FORM_MINIMUM_SIZE) {
        this.previewFormWidth = PREVIEW_EDITOR_FORM_MINIMUM_SIZE
        return
      }

      this.previewFormWidth = width
    },

    setFloating(isFloating: boolean): void {
      this.floating = isFloating
    },

    setSpaceBackgroundTasks(arrayOfTasks: BackgroundTask[]): void {
      this.backgroundTasks = arrayOfTasks
    },

    setEditorFormOpen(isOpen: boolean): void {
      this.editorFormOpen = isOpen
    },

    clearSpace(): void {
      Object.assign(this, defaultState)
    },

    setUserCanSubscribe(value: boolean): void {
      this.canSubscribe = value
    },

    updateSingleRole(role: SpaceRole): void {
      this.model.space_roles = this.space.space_roles.map(function (spaceRole) {
        return spaceRole.id === role.id ? role : spaceRole
      })
    },

    addNewRoleOnState(role: SpaceRole): void {
      this.model.space_roles.push(role)
    },

    removeRoleFromState(roleId: number): void {
      this.model.space_roles = this.space.space_roles.filter(
        (spaceRole) => spaceRole.id !== roleId,
      )
    },

    setCollaborator(collaborator: Collaborator): void {
      const collabIndex = this.space.collaborators.findIndex(
        (collab) => collab.id === collaborator.id,
      )

      if (collabIndex > -1) {
        this.model.collaborators.splice(collabIndex, 1, collaborator)
      } else {
        this.model.collaborators.unshift(collaborator)
      }
    },

    setSpaceStatistics(statistics: SpaceStatistics): void {
      this.spaceStatistics = statistics
    },

    setHasSlackWebhook(has_slack_webhook: boolean): void {
      this.model.has_slack_webhook = has_slack_webhook
    },

    setLastBoostInvoice(limitBoost: LastBoostInvoice): void {
      this.lastBoostInvoice = limitBoost
    },

    hasAcceptedTerms(termId: number): boolean {
      return (
        this.space.disclaimer_ids?.find(
          (disclaimer) => disclaimer === termId,
        ) !== undefined
      )
    },

    async loadSpace(payload: { spaceId: number; region?: RegionServerKeys }) {
      const response = await handleRequest({
        apiCall: (): Promise<AxiosResponse<{ space: Space }>> =>
          client.get(`spaces/${payload.spaceId}`, {
            region: this.region ?? payload?.region,
          }),
        expectedErrors: new Map([[404, t('No space found with this ID.')]]),
      })

      if (response.status === 'success') {
        const { space } = response.data
        this.setSpaceRegion(space.region)
        this.setSpace(space)
        toggleUnleashSpace(space.id)

        const userStore = useUser()
        const users = this.space.collaborators.filter(
          (collaborator: Collaborator) => {
            return (
              collaborator.user && collaborator.user.id === userStore.user.id
            )
          },
        )

        if (!isEmpty(users)) {
          const user = users[0]
          if (user) {
            const hasPermissions = Array.isArray(user.permissions)
            const hasSubscribe = user.permissions.includes(
              Permissions.CanSubscribe,
            )
            this.setUserCanSubscribe(hasPermissions && hasSubscribe)
          }
        }

        Sentry.setTag('space_id', this.space.id)

        this.setSpaceLanguage()
      }

      return response
    },

    async addSpace(payload: CreateSpace): Promise<Space> {
      const sendPayload: CreateSpace = {
        space: {
          name: payload.space.name,
        },
      }

      if (payload.space.is_demo) {
        sendPayload.space.is_demo = true
      }

      if (typeof payload.in_org !== 'undefined') {
        sendPayload.in_org = payload.in_org
      }
      if (payload.dup_id) {
        sendPayload.dup_id = payload.dup_id
      }
      if (payload.space_type) {
        sendPayload.space_type = payload.space_type
      }
      if (payload.space.blueprint_reference) {
        sendPayload.space.blueprint_reference =
          payload.space.blueprint_reference
      }

      const { data } = await client.post(`spaces/`, sendPayload, {
        region: payload.server,
      })

      return data.space
    },

    /**
     * Updates the space data. The `id` passed in the payload, if specified, will be used in the request,
     * otherwise, the current space id from the store will be used.
     * @param payload
     */
    async updateSpace(space: Partial<Space>): Promise<Space> {
      const id = space.id || this.space.id

      const response = await client.put(
        `spaces/${id}`,
        { space },
        {
          region: this.region,
        },
      )
      const data = response.data as unknown as { space: Space }
      this.setSpace(data.space)
      return data.space
    },

    async saveSpace(payload: Space): Promise<Space> {
      const { spaceId, spaceData } = payload
      return await client.put(
        `spaces/${spaceId}`,
        {
          space: spaceData,
        },
        {
          region: this.region,
        },
      )
    },

    async loadInstalledApps(payload: { spaceId: number }) {
      const response = await handleRequest({
        apiCall: (): Promise<
          AxiosResponse<{ app_provisions: AppProvision[] }>
        > =>
          client.get(`spaces/${payload.spaceId}/app_provisions`, {
            region: this.region,
          }),
      })

      if (response.status === 'success') {
        this.setInstalledApps(response.data.app_provisions)
      }

      return response
    },

    async loadStatistics(
      spaceId: number | string | string[],
      signal?: AbortSignal,
      version: string = 'new',
    ): Promise<unknown> {
      const { data: statistics } = await client.get<SpaceStatistics>(
        `spaces/${spaceId}/statistics?version=${version}`,
        { region: this.region, signal },
      )

      if (!isEmpty(statistics.my_content)) {
        const { loadWorkflows, loadWorkflowStageChanges } = useWorkflows()

        await Promise.all([
          loadWorkflows(Number(spaceId)),

          ...statistics.my_content.map(async (content) => {
            try {
              const { data } = await loadWorkflowStageChanges({
                spaceId: spaceId.toString(),
                storyId: content.id,
                perPage: 1,
              })

              content.stage = data.workflow_stage_changes[0]
            } catch (error) {
              console.error(error)
            }
          }),
        ])
      }

      this.setSpaceStatistics(statistics)

      return statistics
    },

    async loadLastLimitBoostInvoice(spaceId: number): Promise<void> {
      const { data } = await client.get(
        `/spaces/${spaceId}/last_limit_boost_invoice`,
        {
          region: this.region,
        },
      )
      this.setLastBoostInvoice(data.limit_boost)
    },

    async loadSpaceBackgroundTasks(spaceId: number | string): Promise<void> {
      const { data }: AxiosResponse<BackgroundTasks> = await client.get(
        `spaces/${spaceId}/background_tasks`,
        {
          region: this.region,
        },
      )
      return this.setSpaceBackgroundTasks(data?.background_tasks || [])
    },

    async loadSpaceBackgroundTaskById(
      spaceId: number | string,
      taskId: number,
    ): Promise<BackgroundTask> {
      const { data }: AxiosResponse<{ background_task: BackgroundTask }> =
        await client.get(`spaces/${spaceId}/background_tasks/${taskId}`, {
          region: this.region,
        })

      return data?.background_task
    },

    async loadApiKeys(
      spaceId: number | string,
    ): Promise<SafeAxiosResponse<{ api_keys: ApiKey[] }>> {
      const { can } = usePermissionsPlugin()

      const canAccessApiKeys =
        can(Permissions.ViewDraftJSON) || can(Permissions.ViewPublishedJSON)

      if (!canAccessApiKeys) {
        return {
          status: 'expectedError',
          error: '', // we do not call the API and there's no need to show an error on the UI
        }
      }

      const response = await handleRequest({
        apiCall: (): Promise<AxiosResponse<{ api_keys: ApiKey[] }>> =>
          client.get(`spaces/${spaceId}/api_keys`, {
            region: this.region,
          }),
        expectedErrors: new Map([[403, '']]),
      })

      if (response.status === 'success') {
        this.setApiKeys(response.data.api_keys)
      }

      return response
    },

    async invalidateImagePaths(payload: {
      invalidations: string[]
    }): Promise<unknown> {
      return await client.post(
        `spaces/${this.space.id}/image_service_invalidations`,
        payload,
        {
          region: this.region,
        },
      )
    },

    async deleteSpace(): Promise<void> {
      return client.delete(`spaces/${this.space.id}`, {
        region: this.region,
      })
    },

    async saveRole(payload: {
      spaceId: number | string
      rolePayload: { space_role: SpaceRole }
      id: number | string
      isNew: boolean
    }): Promise<void> {
      const { spaceId, rolePayload, id, isNew } = payload
      await client.put(`/spaces/${spaceId}/space_roles/${id}`, rolePayload, {
        region: this.region,
      })

      if (isNew) {
        return this.addNewRoleOnState(rolePayload.space_role)
      }

      return this.updateSingleRole(rolePayload.space_role)
    },

    async createRole(payload: {
      spaceId: number | string
      role: string
    }): Promise<SpaceRole> {
      const { spaceId, role } = payload

      const { data } = await client.post(
        `/spaces/${spaceId}/space_roles/`,
        {
          space_role: {
            role,
          },
        },
        {
          region: this.region,
        },
      )

      return data.space_role
    },

    async deleteRole(payload: {
      spaceId: number | string
      roleId: number | string
    }): Promise<void> {
      const { spaceId, roleId } = payload
      await client.delete(`/spaces/${spaceId}/space_roles/${roleId}`, {
        region: this.region,
      })

      return this.removeRoleFromState(roleId)
    },

    async removeCollaborator(payload: {
      userId: number | string
      spaceId: number | string
    }): Promise<void> {
      const { spaceId, userId } = payload

      await client.delete(`/spaces/${spaceId}/collaborators/${userId}`, {
        region: this.region,
      })
    },

    async resendInvite(invite: InvitedSpaceCollaborator): Promise<void> {
      return await client.put(`/invitations/${invite.rid}/renew`)
    },

    async transferOwner(content: {
      payload: { space: { owner_id: number | string } }
      spaceId: number | string
    }): Promise<void> {
      const { payload, spaceId } = content

      await client.put(`/spaces/${spaceId}/`, payload, {
        region: this.region,
      })

      return this.loadSpace(content)
    },

    async addUserToSpace(payload: {
      collaborator: CollaboratorModel
      spaceId: number | string
    }): Promise<void> {
      const { collaborator, spaceId } = payload

      const { data } = await client.post(
        `/spaces/${spaceId}/collaborators`,
        collaborator,
        {
          region: this.region,
        },
      )

      return this.setCollaborator(data.collaborator)
    },

    async editUserInSpace(payload: {
      collaborator: { collaborator: CollaboratorModel }
      spaceId: number | string
      userId: number | string
    }): Promise<void> {
      const { collaborator, spaceId, userId } = payload

      const { data } = await client.put(
        `/spaces/${spaceId}/collaborators/${userId}`,
        collaborator,
        {
          region: this.region,
        },
      )

      return this.setCollaborator(data.collaborator)
    },

    async checkIfUserExistInOrg(userName: string): Promise<boolean> {
      const { data } = await client.get(`/users`, {
        params: {
          search: userName,
        },
        region: this.region,
      })

      return data.users.some(
        ({ username }: { username: string }) => username === userName,
      )
    },

    async createBackup(): Promise<Backups> {
      const { data } = await client.post(
        `/spaces/${this.space.id}/backups/`,
        {},
        {
          region: this.region,
        },
      )

      return data
    },

    async loadAuthAppQrCode(): Promise<{
      svg_qr_code: string
      manual_setup_code: string
    }> {
      const { data } = await client.post('/users/generate_totp_qr_code')

      return data
    },

    async saveAuthAppTwoFactorCode(authCode: string): Promise<void> {
      const { data } = await client.post('/users/verify_qr_code_factor', {
        verification_code: authCode,
      })

      return data
    },
  },
})
